import Tooltip from '@/components/tooltip/tooltip-component';
import { PropTypes } from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { BsAsterisk } from 'react-icons/bs';
import styled from 'styled-components';
import theme from '@/common/theme';

const IsRequiredIcon = styled(BsAsterisk)`
  background-color: ${({ $isRequired, $isHidden }) => {
    if ($isHidden) {
      return theme.COLOR_GREY5;
    }
    return $isRequired ? 'rgba(0, 0, 0, 1)' : 'rgba(234, 237, 243, 1)';
  }};
  color: ${({ $isRequired, $isHidden }) => {
    if ($isHidden) {
      return theme.COLOR_GREY4;
    }
    return $isRequired ? 'rgba(255, 255, 255, 1)' : 'rgba(88, 89, 91, 1)';
  }};
  padding: 3px;
  font-size: 16px;
  border-radius: 50%;
`;

/**
 * Is required icon/input for option row form
 * @param {object} props - The props passed to the OptionMenu component.
 * @param {Array} props.name - An array of menu items.
 * @param {boolean} props.$isHidden - Whether the option is hidden.
 * @param {boolean} props.disabled - Whether the option is disabled.
 * @returns {JSX.Element} The rendered Is Required component.
 */
const IsRequiredInput = ({ name, $isHidden, disabled }) => {
  const { control } = useFormContext();

  const getIconColor = (value) => {
    if ($isHidden) {
      return theme.COLOR_GREY4;
    }
    return value ? 'rgba(255, 255, 255, 1)' : 'rgba(88, 89, 91, 1)';
  };

  return (
    <Controller
      control={control}
      name={`${name}.is_required`}
      render={({ field }) => (
        <Tooltip
          text={
            disabled
              ? 'Cannot change the required status of this option because it is linked to a template'
              : 'Mark this item as required'
          }
          id={`${name}.is_required`}
          icon={
            <IsRequiredIcon
              $isRequired={field?.value}
              $isHidden={$isHidden}
              color={getIconColor(field?.value)}
              onClick={() => {
                if (!disabled) {
                  field.onChange(!field.value);
                }
              }}
            />
          }
        />
      )}
    />
  );
};

IsRequiredInput.defaultProps = {
  $isHidden: false,
  disabled: false,
};

IsRequiredInput.propTypes = {
  name: PropTypes.string.isRequired,
  $isHidden: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default IsRequiredInput;
